/**
 * Parses a range string in the format "min-max" or returns null if not a range.
 * @param str - String to parse
 * @returns Object with min and max, or null if not a range
 */
function parseRange(str: string): { min: number; max: number } | null {
  const match = str.match(/^(.+?)-(.+)$/);
  if (!match) return null;

  const min = Number(match[1].trim());
  const max = Number(match[2].trim());

  if (isNaN(min) || isNaN(max) || !isFinite(min) || !isFinite(max)) {
    return null;
  }

  return { min, max };
}

/**
 * Natural sort function that handles numeric strings and ranges correctly.
 * - Numbers are sorted numerically (1, 2, 10, 11) instead of lexicographically (1, 10, 11, 2)
 * - Range strings like "0-10", "10-20" are sorted by their minimum value
 *
 * @param a - First string to compare
 * @param b - Second string to compare
 * @returns Negative number if a < b, positive if a > b, 0 if equal
 *
 * @example
 * ```ts
 * ["1", "10", "2", "20"].sort(naturalCompare)
 * // Returns: ["1", "2", "10", "20"]
 *
 * ["10-20", "0-10", "30-40"].sort(naturalCompare)
 * // Returns: ["0-10", "10-20", "30-40"]
 * ```
 */
export function naturalCompare(a: string, b: string): number {
  const aStr = String(a).trim();
  const bStr = String(b).trim();

  // Try to parse as ranges first (format: "min-max")
  const aRange = parseRange(aStr);
  const bRange = parseRange(bStr);

  // If both are ranges, compare by minimum value, then by maximum
  if (aRange && bRange) {
    if (aRange.min !== bRange.min) {
      return aRange.min - bRange.min;
    }
    // If minimums are equal, compare by maximum
    return aRange.max - bRange.max;
  }

  // If one is a range and the other isn't, ranges come first
  if (aRange && !bRange) {
    return -1;
  }
  if (bRange && !aRange) {
    return 1;
  }

  // Check if both strings are pure numbers
  const aNum = Number(aStr);
  const bNum = Number(bStr);

  // If both are valid numbers, compare numerically
  if (!isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum)) {
    // Handle the case where the string representation might have leading zeros
    // Compare as numbers first
    if (aNum !== bNum) {
      return aNum - bNum;
    }
    // If numbers are equal, compare string length (shorter = fewer leading zeros)
    return aStr.length - bStr.length;
  }

  // If one is a number and the other isn't, numbers come first
  if (!isNaN(aNum) && isFinite(aNum) && (isNaN(bNum) || !isFinite(bNum))) {
    return -1;
  }
  if (!isNaN(bNum) && isFinite(bNum) && (isNaN(aNum) || !isFinite(aNum))) {
    return 1;
  }

  // Otherwise, use localeCompare for non-numeric strings
  return aStr.localeCompare(bStr);
}

